import React from 'react';
import { useApp } from '../../context/AppContext';
import { 
  TrendingUp,
  TrendingDown,
  Users,
  Heart,
  UserCheck,
  DollarSign,
  AlertTriangle,
  Activity,
  Globe,
  Calendar,
  BarChart3,
  PieChart
} from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const monthLabel = (date) => date.toLocaleString('en-US', { month: 'short' });
const monthKey = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${date.getMonth()}`;
};

const lastSixMonths = () => {
  const now = new Date();
  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    return { key: monthKey(date), month: monthLabel(date) };
  });
};

const buildMonthlyTrends = ({ subscribers, dependants, visits, metrics }) => (
  lastSixMonths().map(item => ({
    month: item.month,
    subscribers: subscribers.filter(sub => monthKey(sub.createdAt || sub.created_at || sub.joinedDate) === item.key).length,
    dependants: dependants.filter(dep => monthKey(dep.createdAt || dep.created_at || dep.enrolledDate) === item.key).length,
    visits: visits.filter(visit => monthKey(visit.scheduledStart || visit.scheduled_start || visit.scheduledDate || visit.scheduled_date) === item.key).length,
    revenue: item.key === lastSixMonths().at(-1).key ? Number(metrics?.mrr || 0) : 0,
  }))
);

const buildWellnessDistribution = (dependants) => {
  const buckets = [
    { range: '90-100', min: 90, max: 100, color: '#22c55e' },
    { range: '75-89', min: 75, max: 89, color: '#84cc16' },
    { range: '50-74', min: 50, max: 74, color: '#eab308' },
    { range: '25-49', min: 25, max: 49, color: '#f97316' },
    { range: '0-24', min: 0, max: 24, color: '#ef4444' },
  ];
  const scores = dependants
    .map(item => item.overallWellnessScore ?? item.overall_wellness_score)
    .map(Number)
    .filter(value => Number.isFinite(value));
  return buckets.map(bucket => ({
    ...bucket,
    count: scores.filter(score => score >= bucket.min && score <= bucket.max).length,
  }));
};

const buildAlertTypes = (alerts) => {
  const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#9F1D35'];
  const counts = alerts.reduce((acc, alert) => {
    const category = alert.category || alert.type || 'Other';
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts).map(([name, value], index) => ({ name, value, color: colors[index % colors.length] }));
};

const buildRegionalData = ({ subscribers, careOfficers, visits, dependants }) => {
  const regions = new Map();
  const ensureRegion = (region) => {
    const key = region || 'Unassigned';
    if (!regions.has(key)) {
      regions.set(key, { region: key, subscribers: 0, officers: 0, visits: 0, scores: [] });
    }
    return regions.get(key);
  };
  subscribers.forEach(sub => ensureRegion(sub.city || sub.country || 'Unassigned').subscribers += 1);
  careOfficers.forEach(officer => {
    const coverage = officer.coverageZone || officer.coverage_zone || {};
    ensureRegion(coverage.state || officer.state || officer.assignedRegion || officer.assigned_region || 'Unassigned').officers += 1;
  });
  visits.forEach(visit => {
    const dependant = dependants.find(dep => (dep.id || dep._id) === (visit.dependantId || visit.dependant_id));
    const address = dependant?.address || visit.dependantSnapshot?.address || visit.dependant_snapshot?.address || {};
    const region = ensureRegion(address.state || address.lga || 'Unassigned');
    region.visits += 1;
    const score = Number(visit.overallWellnessScore ?? visit.overall_wellness_score);
    if (Number.isFinite(score)) region.scores.push(score);
  });
  return Array.from(regions.values()).map(region => ({
    ...region,
    avgWellness: region.scores.length
      ? Number((region.scores.reduce((sum, score) => sum + score, 0) / region.scores.length).toFixed(1))
      : 0,
  }));
};

// Stat Card Component
const StatCard = ({ label, value, change, changeType = 'positive', icon: Icon, color = 'burgundy' }) => {
  const colorStyles = {
    burgundy: 'border-burgundy-100 bg-burgundy-50/50',
    green: 'border-green-100 bg-green-50/50',
    blue: 'border-blue-100 bg-blue-50/50',
    red: 'border-red-100 bg-red-50/50',
    yellow: 'border-yellow-100 bg-yellow-50/50',
  };

  const iconColors = {
    burgundy: 'text-burgundy-600',
    green: 'text-green-600',
    blue: 'text-blue-600',
    red: 'text-red-600',
    yellow: 'text-yellow-600',
  };

  return (
    <div className={`bg-white rounded-2xl border ${colorStyles[color]} p-5`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-xl ${colorStyles[color]}`}>
          <Icon className={`w-5 h-5 ${iconColors[color]}`} />
        </div>
        {change && (
          <span className={`text-xs font-medium flex items-center ${changeType === 'positive' ? 'text-green-600' : 'text-red-600'}`}>
            {changeType === 'positive' ? <TrendingUp className="w-3 h-3 mr-0.5" /> : <TrendingDown className="w-3 h-3 mr-0.5" />}
            {change}
          </span>
        )}
      </div>
      <p className="text-3xl font-serif font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
    </div>
  );
};

const AnalyticsPage = () => {
  const { metrics, subscribers, dependants, careOfficers, visits, alerts } = useApp();
  const monthlyTrendsData = buildMonthlyTrends({ subscribers, dependants, visits, metrics });
  const wellnessDistribution = buildWellnessDistribution(dependants);
  const wellnessScoreTotal = wellnessDistribution.reduce((sum, item) => sum + item.count, 0);
  const alertTypesData = buildAlertTypes(alerts);
  const regionalData = buildRegionalData({ subscribers, careOfficers, visits, dependants });
  const activeAlerts = alerts.filter(alert => !['Resolved', 'Acknowledged', 'Suppressed'].includes(alert.status)).length;
  const completedVisits = visits.filter(visit => ['Approved', 'Submitted', 'Flagged', 'Rejected'].includes(visit.status)).length;
  const completionRate = visits.length ? ((completedVisits / visits.length) * 100).toFixed(1) : '0.0';
  const durations = visits.map(visit => Number(visit.duration)).filter(value => Number.isFinite(value) && value > 0);
  const avgDuration = durations.length ? Math.round(durations.reduce((sum, value) => sum + value, 0) / durations.length) : 0;
  const viewedReports = visits.filter(visit => visit.subscriberViewed || visit.subscriber_viewed).length;
  const reportViewRate = visits.length ? ((viewedReports / visits.length) * 100).toFixed(1) : '0.0';

  return (
    <div className="min-h-screen bg-cream" data-testid="analytics-page">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-serif text-2xl font-bold text-gray-900">Platform Analytics</h1>
              <p className="text-sm text-gray-500 mt-1">Comprehensive insights and performance metrics</p>
            </div>
            <div className="flex items-center gap-3">
              <select className="px-4 py-2 text-sm border border-gray-200 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-burgundy-500">
                <option>Last 6 Months</option>
                <option>Last 3 Months</option>
                <option>Last 30 Days</option>
                <option>This Year</option>
              </select>
              <button className="px-4 py-2 bg-burgundy-600 text-[#FAFAF7] text-sm font-medium rounded-full hover:bg-burgundy-700 transition-colors">
                Export Report
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard label="Active Subscribers" value={metrics?.totalActiveSubscribers ?? subscribers.length} change={metrics?.monthlyGrowthRate ? `+${metrics.monthlyGrowthRate}%` : null} icon={Users} color="burgundy" />
          <StatCard label="Dependants" value={metrics?.totalDependantsEnrolled ?? dependants.length} icon={Heart} color="red" />
          <StatCard label="Care Officers" value={metrics?.activeCareOfficers ?? careOfficers.length} icon={UserCheck} color="green" />
          <StatCard label="MRR" value={`$${Number(metrics?.mrr || 0).toLocaleString()}`} icon={DollarSign} color="green" />
          <StatCard label="Avg Wellness" value={metrics?.averageWellnessScore ?? '0'} icon={Activity} color="blue" />
          <StatCard label="Active Alerts" value={activeAlerts} icon={AlertTriangle} color="yellow" />
        </div>

        {/* Growth Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Subscriber & Dependant Growth */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-serif text-lg font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-gray-400" />
                User Growth Trend
              </h3>
            </div>
            <div className="p-5">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={monthlyTrendsData}>
                    <defs>
                      <linearGradient id="subscriberGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#9F1D35" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#9F1D35" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="dependantGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} />
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px' }} />
                    <Legend />
                    <Area type="monotone" dataKey="subscribers" name="Subscribers" stroke="#9F1D35" strokeWidth={2} fill="url(#subscriberGradient)" />
                    <Area type="monotone" dataKey="dependants" name="Dependants" stroke="#ef4444" strokeWidth={2} fill="url(#dependantGradient)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Revenue Trend */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-serif text-lg font-semibold text-gray-900 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-gray-400" />
                Revenue Trend (USD)
              </h3>
            </div>
            <div className="p-5">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyTrendsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
                    <Tooltip 
                      contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px' }}
                      formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']}
                    />
                    <Bar dataKey="revenue" fill="#22c55e" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Charts Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Wellness Distribution */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-serif text-lg font-semibold text-gray-900">Wellness Score Distribution</h3>
            </div>
            <div className="p-5">
              <div className="space-y-3">
                {wellnessDistribution.map((item) => (
                  <div key={item.range}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-gray-600">Score {item.range}</span>
                      <span className="font-medium text-gray-900">{item.count} dependants</span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all" 
                        style={{ width: `${Math.min(100, (item.count / Math.max(1, wellnessScoreTotal)) * 100)}%`, backgroundColor: item.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Alert Types */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-serif text-lg font-semibold text-gray-900">Alert Categories</h3>
            </div>
            <div className="p-5">
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={alertTypesData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={75}
                      paddingAngle={4}
                      dataKey="value"
                    >
                      {alertTypesData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-3 mt-3">
                {alertTypesData.map((item) => (
                  <div key={item.name} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-xs text-gray-600">{item.name} ({item.value})</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Visit Completion Rate */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-serif text-lg font-semibold text-gray-900">Visit Metrics</h3>
            </div>
            <div className="p-5">
              <div className="space-y-5">
                <div className="text-center p-4 bg-green-50 rounded-xl">
                  <p className="text-4xl font-serif font-bold text-green-600">{completionRate}%</p>
                  <p className="text-sm text-gray-600 mt-1">Completion Rate</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-gray-50 rounded-xl">
                    <p className="text-2xl font-bold text-gray-900">{avgDuration}</p>
                    <p className="text-xs text-gray-500">Avg Duration (min)</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-xl">
                    <p className="text-2xl font-bold text-gray-900">{reportViewRate}%</p>
                    <p className="text-xs text-gray-500">Report View Rate</p>
                  </div>
                </div>
                <div className="text-center p-3 bg-burgundy-50 rounded-xl">
                  <p className="text-2xl font-bold text-burgundy-600">{metrics?.nps ?? 0}</p>
                  <p className="text-xs text-gray-600">Net Promoter Score</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Regional Performance Table */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h3 className="font-serif text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Globe className="w-5 h-5 text-gray-400" />
                Regional Performance
              </h3>
              <p className="text-sm text-gray-500">Performance breakdown by region</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Region</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscribers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Care Officers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Wellness</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Visits</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {regionalData.map((region) => (
                  <tr key={region.region} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-gray-900">{region.region}</td>
                    <td className="px-6 py-4 text-gray-600">{region.subscribers}</td>
                    <td className="px-6 py-4 text-gray-600">{region.officers}</td>
                    <td className="px-6 py-4">
                      <span className={`font-medium ${region.avgWellness >= 75 ? 'text-green-600' : region.avgWellness >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {region.avgWellness}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{region.visits.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        Active
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
